<template>
	<div class="bene-page" :style="stageStyle">
		<NavMenu></NavMenu>

		<div class="stage-shell">
			<div class="route-stage">
				<div class="location-state">{{ locationState }}</div>

				<img
					class="map-background"
					src="/img/bg.png"
					srcset="/img/bg.png 1x, /img/bg2x.png 2x"
					alt=""
					aria-hidden="true"
					draggable="false"
				>

				<svg class="route-svg" viewBox="0 0 375 812" preserveAspectRatio="none" aria-hidden="true">
					<defs>
						<linearGradient id="beneRouteGradient" x1="282" y1="506" x2="102" y2="216" gradientUnits="userSpaceOnUse">
							<stop offset="0%" stop-color="#f2ffb7" />
							<stop offset="55%" stop-color="#e7ff91" />
							<stop offset="100%" stop-color="#d7ff68" />
						</linearGradient>
						<filter id="beneGlow" x="-60%" y="-60%" width="220%" height="220%">
							<feGaussianBlur stdDeviation="3.5" result="coloredBlur" />
							<feMerge>
								<feMergeNode in="coloredBlur" />
								<feMergeNode in="SourceGraphic" />
							</feMerge>
						</filter>
					</defs>
					<path
						id="bene-route-path"
						class="route-shadow"
						d="M282 506 C258 448 228 432 199 397 C171 365 167 346 155 287 C148 253 140 224 102 216"
					/>
					<path
						id="bene-route-path-reverse"
						class="route-guide"
						d="M282 506 C258 448 228 432 199 397 C171 365 167 346 155 287 C148 253 140 224 102 216"
					/>
					<path
						class="route-base"
						d="M282 506 C258 448 228 432 199 397 C171 365 167 346 155 287 C148 253 140 224 102 216"
					/>
					<path
						class="route-flow"
						d="M282 506 C258 448 228 432 199 397 C171 365 167 346 155 287 C148 253 140 224 102 216"
					/>
					<circle class="point-ring target-ring" cx="102" cy="216" r="28" />
					<circle class="point-ring current-ring" cx="282" cy="506" r="28" />
					<circle class="point-halo target-halo" cx="102" cy="216" r="14" />
					<circle class="point-halo current-halo" cx="282" cy="506" r="14" />
					<circle class="point-core target-core" cx="102" cy="216" r="6" />
					<circle class="point-core current-core" cx="282" cy="506" r="6" />
					<circle class="route-runner" r="2.2">
						<animateMotion dur="4.2s" repeatCount="indefinite">
							<mpath href="#bene-route-path-reverse" />
						</animateMotion>
					</circle>
				</svg>

				<div class="route-name label-target">冈仁波齐</div>
				<div class="route-name label-current">我</div>

				<div class="distance-panel">
					<div class="distance-stack">
						<div class="distance-value">
							<span>{{ formattedDistance }}</span>
							<em>km</em>
						</div>
						<div class="distance-caption">距离另一个我</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import NavMenu from '../components/nav.vue';

	const KAILASH = {
		lat: 31.0675,
		lng: 81.3119,
	};

	const DEFAULT_LOCATION = {
		lat: 39.9042,
		lng: 116.4074,
	};

	const DESIGN_WIDTH = 375;
	const DESIGN_HEIGHT = 812;
	const MAX_STAGE_WIDTH = 430;
	const VIEWPORT_EDGE_GAP = 0;
	const LABEL_GAP = 5;

	export default {
		name: 'bene',
		components: {
			NavMenu,
		},
		data() {
			return {
				currentPosition: null,
				displayDistance: 0,
				locationState: '已根据当前位置动态计算直线距离',
				watchId: null,
				animationId: null,
				geoSettled: false,
				previousHtmlBackground: '',
				previousBodyBackground: '',
				viewportWidth: DESIGN_WIDTH,
				viewportHeight: DESIGN_HEIGHT,
			}
		},
		computed: {
			stageStyle() {
				const viewportWidth = Math.max(this.viewportWidth, 1);
				const viewportHeight = Math.max(this.viewportHeight, 1);
				const maxWidthScale = MAX_STAGE_WIDTH / DESIGN_WIDTH;
				const stageScale = Math.min(
					viewportWidth / DESIGN_WIDTH,
					(viewportHeight - VIEWPORT_EDGE_GAP) / DESIGN_HEIGHT,
					maxWidthScale
				);
				const stageWidth = DESIGN_WIDTH * stageScale;
				const stageHeight = DESIGN_HEIGHT * stageScale;
				const centeredTop = (viewportHeight - stageHeight) / 2;
				const stageTop = Math.max(centeredTop, 0);

				return {
					'--stage-scale': stageScale,
					'--stage-width': `${stageWidth}px`,
					'--stage-height': `${stageHeight}px`,
					'--stage-top': `${stageTop}px`,
					'--nav-top': `${stageTop + (50 * stageScale)}px`,
					'--nav-icon-width': `${18 * stageScale}px`,
					'--nav-icon-height': `${15 * stageScale}px`,
					'--distance-x-scale': this.distanceXScale,
					'--point-label-gap': `${LABEL_GAP / stageScale}px`,
				};
			},
			distanceXScale() {
				const length = this.formattedDistance.length;

				if (length >= 8) {
					return 0.6;
				}

				if (length >= 7) {
					return 0.7;
				}

				if (length >= 6) {
					return 0.78;
				}

				return 1;
			},
			formattedDistance() {
				return this.displayDistance.toFixed(2);
			},
			currentPositionText() {
				if (!this.currentPosition) {
					return '定位中';
				}

				return `${this.formatCoordinate(this.currentPosition.lat, 'N', 'S')}, ${this.formatCoordinate(this.currentPosition.lng, 'E', 'W')}`;
			},
		},
		mounted() {
			this.previousHtmlBackground = document.documentElement.style.backgroundColor;
			this.previousBodyBackground = document.body.style.backgroundColor;
			document.documentElement.style.backgroundColor = '#000';
			document.body.style.backgroundColor = '#000';
			this.updateViewport();
			window.addEventListener('resize', this.updateViewport);
			window.addEventListener('orientationchange', this.updateViewport);

			if (window.visualViewport) {
				window.visualViewport.addEventListener('resize', this.updateViewport);
				window.visualViewport.addEventListener('scroll', this.updateViewport);
			}

			this.setDistance(DEFAULT_LOCATION);
			this.startLocationWatch();
		},
		beforeDestroy() {
			window.removeEventListener('resize', this.updateViewport);
			window.removeEventListener('orientationchange', this.updateViewport);

			if (window.visualViewport) {
				window.visualViewport.removeEventListener('resize', this.updateViewport);
				window.visualViewport.removeEventListener('scroll', this.updateViewport);
			}

			if (this.watchId !== null && navigator.geolocation) {
				navigator.geolocation.clearWatch(this.watchId);
			}

			if (this.animationId) {
				cancelAnimationFrame(this.animationId);
			}

			document.documentElement.style.backgroundColor = this.previousHtmlBackground;
			document.body.style.backgroundColor = this.previousBodyBackground;
		},
		methods: {
			updateViewport() {
				const visualViewport = window.visualViewport;
				this.viewportWidth = visualViewport ? visualViewport.width : window.innerWidth;
				this.viewportHeight = visualViewport ? visualViewport.height : window.innerHeight;
			},
			startLocationWatch() {
				if (!navigator.geolocation) {
					this.useDefaultLocation();
					return;
				}

				this.watchId = navigator.geolocation.watchPosition(
					this.handlePosition,
					this.handleLocationError,
					{
						enableHighAccuracy: true,
						timeout: 12000,
						maximumAge: 60000,
					}
				);
			},
			handlePosition(position) {
				const coords = {
					lat: position.coords.latitude,
					lng: position.coords.longitude,
				};

				this.geoSettled = true;
				this.currentPosition = coords;
				this.setDistance(coords);
			},
			handleLocationError() {
				if (this.geoSettled) {
					return;
				}

				this.useDefaultLocation();
			},
			useDefaultLocation() {
				this.geoSettled = true;
				this.currentPosition = DEFAULT_LOCATION;
				this.setDistance(DEFAULT_LOCATION);
			},
			setDistance(from) {
				const nextDistance = this.getDistance(from, KAILASH);
				this.animateDistance(nextDistance);
			},
			getDistance(from, to) {
				const radius = 6371.0088;
				const fromLat = this.toRadians(from.lat);
				const toLat = this.toRadians(to.lat);
				const deltaLat = this.toRadians(to.lat - from.lat);
				const deltaLng = this.toRadians(to.lng - from.lng);
				const value = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2)
					+ Math.cos(fromLat) * Math.cos(toLat)
					* Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
				const centralAngle = 2 * Math.atan2(Math.sqrt(value), Math.sqrt(1 - value));

				return radius * centralAngle;
			},
			animateDistance(nextDistance) {
				if (this.animationId) {
					cancelAnimationFrame(this.animationId);
				}

				const startValue = this.displayDistance;
				const change = nextDistance - startValue;
				const duration = 1400;
				const startTime = performance.now();

				const step = (now) => {
					const progress = Math.min((now - startTime) / duration, 1);
					const eased = 1 - Math.pow(1 - progress, 3);
					this.displayDistance = startValue + change * eased;

					if (progress < 1) {
						this.animationId = requestAnimationFrame(step);
					} else {
						this.displayDistance = nextDistance;
					}
				};

				this.animationId = requestAnimationFrame(step);
			},
			toRadians(degree) {
				return degree * Math.PI / 180;
			},
			formatCoordinate(value, positive, negative) {
				const direction = value >= 0 ? positive : negative;
				return `${Math.abs(value).toFixed(3)}°${direction}`;
			},
		}
	}
</script>

<style scoped>
	.bene-page {
		background: #000;
		color: #fff;
		width: 100%;
		height: 100vh;
		height: 100dvh;
		min-height: 100vh;
		position: fixed;
		inset: 0;
		overflow: hidden;
		--stage-scale: 1;
		--stage-width: 375px;
		--stage-height: 812px;
		--stage-top: 0px;
		--nav-top: 50px;
		--nav-icon-width: 18px;
		--nav-icon-height: 15px;
		--distance-x-scale: 1;
		--point-label-gap: 5px;
	}

	.bene-page ::v-deep .menu img {
		top: var(--nav-top);
		width: var(--nav-icon-width);
		height: var(--nav-icon-height);
	}

	.stage-shell {
		position: absolute;
		left: 50%;
		top: var(--stage-top);
		width: var(--stage-width);
		height: var(--stage-height);
		overflow: hidden;
		transform: translateX(-50%);
	}

	@media (min-width: 700px) {
		.stage-shell {
			left: 50%;
		}
	}

	.location-state {
		position: absolute;
		z-index: 2;
		top: 94px;
		left: 50%;
		width: 330px;
		transform: translateX(-50%);
		color: #333333;
		font-size: 12px;
		line-height: 1.4;
		text-align: center;
	}

	.route-stage {
		position: absolute;
		z-index: 1;
		top: 0;
		left: 0;
		width: 375px;
		height: 812px;
		transform: scale(var(--stage-scale));
		transform-origin: top left;
		pointer-events: none;
		--target-point-x: 102px;
		--target-point-y: 216px;
		--current-point-x: 282px;
		--current-point-y: 506px;
		--point-halo-radius: 14px;
	}

	.map-background {
		position: absolute;
		inset: 0;
		width: 375px;
		height: 812px;
		display: block;
		object-fit: fill;
		user-select: none;
	}

	.route-svg {
		position: absolute;
		inset: 0;
		width: 375px;
		height: 812px;
		overflow: visible;
	}

	.route-guide,
	.route-shadow,
	.route-base,
	.route-flow {
		fill: none;
		stroke-linecap: round;
		stroke-linejoin: round;
	}

	.route-guide {
		stroke: transparent;
		stroke-width: 1;
	}

	.route-shadow {
		stroke: rgba(216, 255, 106, .18);
		stroke-width: 9;
		filter: url(#beneGlow);
	}

	.route-base {
		stroke: url(#beneRouteGradient);
		stroke-width: 5;
		filter: url(#beneGlow);
		stroke-dasharray: 620;
		stroke-dashoffset: 620;
		animation: routeDraw 1.9s ease-out forwards;
	}

	.route-flow {
		stroke: rgba(255, 255, 255, .42);
		stroke-width: .75;
		stroke-dasharray: 14 24;
		animation: routeFlow 1.25s linear infinite;
	}

	.point-ring {
		fill: rgba(218, 255, 105, .12);
		stroke: none;
		opacity: .82;
		transform-box: fill-box;
		transform-origin: center;
		animation: pointPulse 2.4s ease-out infinite;
	}

	.target-ring {
		animation-delay: .45s;
	}

	.point-halo {
		fill: rgba(220, 255, 111, .22);
	}

	.point-core {
		filter: url(#beneGlow);
	}

	.current-core,
	.target-core {
		fill: #dfff72;
	}

	.route-runner {
		fill: #f7ffd0;
		filter: url(#beneGlow);
		opacity: .68;
	}

	.route-name {
		position: absolute;
		z-index: 2;
		color: #fff;
		font-size: 16px;
		font-weight: 600;
		line-height: 1.2;
		text-shadow: 0 8px 18px rgba(0, 0, 0, .45);
		white-space: nowrap;
	}

	.label-current {
		left: calc(var(--current-point-x) + var(--point-halo-radius) + var(--point-label-gap));
		top: var(--current-point-y);
		transform: translateY(-50%);
	}

	.label-target {
		left: var(--target-point-x);
		top: calc(var(--target-point-y) - var(--point-halo-radius) - var(--point-label-gap));
		transform: translate(-50%, -100%);
	}

	.distance-panel {
		position: absolute;
		z-index: 2;
		left: 0;
		top: 624px;
		width: 375px;
		text-align: left;
		padding: 0 40px;
		box-sizing: border-box;
	}

	.distance-stack {
		display: inline-block;
		text-align: left;
	}

	.distance-value {
		display: inline-flex;
		align-items: baseline;
		justify-content: flex-start;
		gap: 10px;
		font-weight: 700;
		color: #fff;
		text-shadow: 0 10px 30px rgba(0, 0, 0, .45);
		transform: scaleX(var(--distance-x-scale));
		transform-origin: left bottom;
		white-space: nowrap;
	}

	.distance-value span {
		font-size: 82px;
		line-height: .92;
		letter-spacing: 0;
	}

	.distance-value em {
		font-size: 20px;
		font-style: normal;
	}

	.distance-caption {
		margin-top: 12px;
		color: rgba(255, 255, 255, .62);
		font-size: 18px;
		letter-spacing: 0;
		text-align: left;
	}

	@keyframes routeDraw {
		to {
			stroke-dashoffset: 0;
		}
	}

	@keyframes routeFlow {
		to {
			stroke-dashoffset: -38;
		}
	}

	@keyframes pointPulse {
		0% {
			opacity: .66;
			transform: scale(.8);
		}
		70% {
			opacity: .12;
			transform: scale(1.32);
		}
		100% {
			opacity: 0;
			transform: scale(1.48);
		}
	}

	@media (max-width: 360px) {
		.distance-panel {
			padding: 0 36px;
		}

	}
</style>
